import React, { useState } from "react";
import { Icon, Popup, Label, Checkbox, Flag } from "semantic-ui-react";
import AnalysisWarnings from "./AnalysisWarnings";

const styles = {
	circleWrapper: {
		width: 60,
		position: "relative",
		textAlign: "center",
		display: "flex",
		flexDirection: "column",
		justifyContent: "flex-start",
		alignItems: "center",
		marginBottom: 8,
	},
	circleAnalysis: {
		height: 50,
		width: 50,
		borderRadius: 25,
		border: "1px solid #5f9be0",
		textAlign: "center",
		lineHeight: "48px",
		fontWeight: 600,
		color: "#5f9be0",
		cursor: "pointer",
		fontSize: "0.7em",
		boxShadow: "rgba(0, 0, 0, 0.14) 0px -5px 17px -5px inset, rgb(148, 148, 148) 0px 9px 13px -10px",
		marginBottom: 5,
	},
	circleTitle: {
		fontSize: "0.9em",
		fontWeight: "600",
		lineHeight: "1em",
		display: "flex",
		justifyContent: "center",
		wordBreak: "break-word",
		width: 50,
	},
	alertBadge: {
		position: "absolute",
		top: "-5px",
		right: 0,
		height: 20,
		width: 20,
		borderRadius: 10,
		backgroundColor: "#9f0009",
		border: "1px solid #fff",
	},
	arrayDisplay: {
		display: "flex",
		justifyContent: "flex-start",
		padding: "0px",
		width: "100%",
		backgroundColor: "whitesmoke",
		margin: "1px 0",
		fontSize: "0.9em",
		alignItems: "center",
	},
	arrayDisplayCode: {
		width: "100px",
		textAlign: "left",
		fontWeight: "bold",
	},

	arrayRefusedDisplay: {
		width: "100%",
		textAlign: "end",
		fontWeight: "bold",
	},

	simpleDisplay: {},
	onclickCursor: {
		cursor: "help",
	},
};

const Analysis = ({
	analysis,
	onClick,
	isHome = false,
	showLabel = false,
	showWarning = false,
	stability_delay = false,
	alertBadgeStyle = {},
	urgencyPretreatementLimit = 0,
	isMobile = false,
	showRefused = false,
	isRefused = false,
	toggleRefused = null,
	disabled = false,
	withCheckBox = false,
	checkAnalysis = null,
}) => {
	const analysisProperties = analysis.properties ?? analysis;
	const [checked, setChecked] = useState(!!analysis.checked_by);
	const splitDescription = (desc, delimiter) => {
		return desc
			.split(delimiter)
			.filter((item) => item.trim() !== "")
			.map((item, idx) => <i key={`i` + item + idx}>{item.trim()}</i>)
			.reduce((prev, curr) => [prev, ", ", curr]);
	};
	const onClickOnAnalysis = (e) => {
		e?.preventDefault();
		if (onClick) onClick(analysisProperties);
	};
	const circleAnalysis = (
		<>
			<div
				style={{ ...(showLabel ? styles.arrayDisplay : styles.simpleDisplay), ...(showRefused ? styles.arrayRefusedDisplay : styles.simpleDisplay) }}
			>
				<div style={{ ...(showLabel ? styles.arrayDisplay : styles.simpleDisplay), ...(onClick ? styles.onclickCursor : styles.simpleDisplay) }}>
					<div style={showLabel ? styles.arrayDisplayCode : styles.simpleDisplay}>
						{withCheckBox && (
							<Checkbox
								checked={checked}
								label={analysisProperties.code ?? analysis.properties.code}
								style={{ paddingTop: "5px", marginRight: "5px", fontSize: "0.9rem" }}
								onClick={async (e, data) => {
									if (e !== undefined) e.preventDefault();
									if (checkAnalysis) checkAnalysis(data.checked);
									setChecked(data.checked);
								}}
							/>
						)}
						{!withCheckBox && <span onClick={onClickOnAnalysis}>{analysisProperties.code ?? analysis.properties.code}</span>}
					</div>
					{showLabel && (
						<>
							<div style={{ width: !isMobile ? "40%" : "215px" }}>
								<span onClick={onClickOnAnalysis}>
									<Label color="teal">{analysisProperties.label}</Label>
								</span>
								{stability_delay &&
									analysisProperties.pretreatment_stability > 0 &&
									analysisProperties.pretreatment_stability <= urgencyPretreatementLimit && (
										<Popup
											trigger={
												<Label style={{ marginLeft: "1em" }} size="mini" horizontal color="red">
													<Icon name="hourglass" />{" "}
													{analysisProperties.pretreatment_stability > 59
														? `${analysisProperties.pretreatment_stability / 60}h `
														: `${analysisProperties.pretreatment_stability} min`}
												</Label>
											}
											content={<span>Note : {analysisProperties.pretreatment_note ?? "Attention au délai de stabilité"}</span>}
										/>
									)}
							</div>
							{analysisProperties.description && (
								<div style={{ paddingLeft: "20px", width: !isMobile ? "70%" : "215px" }}>
									<span onClick={onClickOnAnalysis}>
										<Popup
											basic
											content={`Synonymes de l'analyse ${analysisProperties?.label}`}
											trigger={
												<div
													style={{
														display: "flex",
														flexDirection: "column",
													}}
												>
													<div>
														<Icon name="comment alternate outline" />
														{splitDescription(analysisProperties.description, ";")}
													</div>
													<div
														style={{
															display: "flex",
															flexDirection: "row",
															flexWrap: "wrap",
															gap: isMobile ? "10px" : "20px",
														}}
													>
														{(analysisProperties.label_en || analysisProperties.synonymous_en?.length > 0) && (
															<div>
																<Flag name="gb" />
																{analysisProperties.label_en}
																{analysisProperties.synonymous_en && (
																	<>
																		{` ( `}
																		{splitDescription(analysisProperties.synonymous_en, ";")}
																		{` )`}
																	</>
																)}
															</div>
														)}
														{(analysisProperties.label_de || analysisProperties.synonymous_de?.length > 0) && (
															<div>
																<Flag name="de" />
																{analysisProperties.label_de}
																{analysisProperties.synonymous_de && (
																	<>
																		{` ( `}
																		{splitDescription(analysisProperties.synonymous_de, ";")}
																		{` )`}
																	</>
																)}
															</div>
														)}
													</div>
												</div>
											}
										/>
									</span>
								</div>
							)}
						</>
					)}
				</div>
				{showRefused && (
					<div style={{ width: "100px" }}>
						{!isRefused && (
							<>
								<Icon name="x" color="red" /> Refusée par patient
								{toggleRefused && !disabled && (
									<Label as="a" size="mini" onClick={toggleRefused ? () => toggleRefused() : null}>
										<Icon name="undo" />
										Annuler le refus
									</Label>
								)}
							</>
						)}
						{isRefused && (
							<Label as={toggleRefused && !disabled ? "a" : "span"} size="mini" onClick={() => toggleRefused(analysis.id)}>
								<Icon name="x" /> Refuser{" "}
							</Label>
						)}
					</div>
				)}
			</div>

			{showWarning &&
				(analysisProperties.urgent ||
					analysisProperties.special_diet ||
					(isHome && (analysisProperties.home_prohibited || analysisProperties.home_not_recommended)) ||
					analysisProperties.fasting ||
					analysisProperties.light_protected ||
					analysisProperties.required_consent ||
					(analysisProperties.taking_note !== "" && analysisProperties.taking_note !== null) ||
					analysisProperties.subcontracting) && (
					<Popup
						trigger={
							<div style={{ ...styles.alertBadge, ...alertBadgeStyle }}>
								<Icon
									size="small"
									name="warning"
									style={{
										position: "relative",
										bottom: 2,
										marginRight: 0,
										color: "#fff",
									}}
								/>
							</div>
						}
						content={<AnalysisWarnings analysis={analysisProperties} isHome={isHome} />}
						basic
						position="top center"
					/>
				)}
		</>
	);

	return onClick !== null && onClick !== undefined && !withCheckBox ? (
		<div
			//style={{ ...styles.circleWrapper, ...circleWrapperStyle }}
			onClick={onClickOnAnalysis}
		>
			{circleAnalysis}
		</div>
	) : (
		<div>{circleAnalysis}</div>
	);
};

export default Analysis;
